import React, { useEffect, useState } from 'react';
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import 'swiper/css';
import { Download, ShoppingBag, Check, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { api } from '../api/client';
import type { ProductItem } from '../api/types';
import { useCart } from '../context/CartContext';


const ProductDetail: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const initial: ProductItem | undefined = (location.state as { product?: ProductItem } | null)?.product;
  const [product, setProduct] = useState<ProductItem | undefined>(initial);
  const [loading, setLoading] = useState(!initial);
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [addedToCart, setAddedToCart] = useState(false);

  useEffect(() => {
    if (initial) return;
    const load = async () => {
      try {
        setLoading(true);
        const products = await api.products();
        const found = products.find((p: ProductItem) => p.squareProductId === id);
        setProduct(found);
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Failed to load product.';
        console.error(msg);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [initial, id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-[#D4A5A5]">Loading...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-serif text-2xl text-[#3D3D3D] mb-4">Product not found</h2>
          <Link to="/products" className="inline-block">
            <button className="bg-[#3D3D3D] hover:bg-[#2D2D2D] text-white rounded-full px-6 py-3 font-sans">Back to Shop</button>
          </Link>
        </div>
      </div>
    );
  }

  const images = product.imageUrls && product.imageUrls.length > 0 ? product.imageUrls : [''];

  const handleAddToCart = () => {
    addToCart({
      id: String(product.squareProductId),
      type: 'product',
      title: product.title,
      price: product.price,
      imageUrl: images[0],
      locationId: product.locationId,
      quantity: 1,
    });
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const nextImage = () => setSelectedImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setSelectedImageIndex((prev) => (prev - 1 + images.length) % images.length);

  return (
    <div className="min-h-screen bg-[#FDF8F5]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/products" className="inline-flex items-center gap-2 text-[#3D3D3D]/70 hover:text-[#D4A5A5] font-sans mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Shop
        </Link>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
            <div className="relative rounded-3xl overflow-hidden bg-white shadow-lg mb-4">
              <motion.img
                key={selectedImageIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                src={images[selectedImageIndex]}
                alt={`${product.title} - Image ${selectedImageIndex + 1}`}
                className="w-full h-[600px] object-cover"
              />

              {product.category && (
                <div className="absolute top-6 left-6">
                  <span className="bg-white/90 backdrop-blur-sm text-[#3D3D3D] px-4 py-2 rounded-full font-sans text-sm font-medium capitalize">
                    {String(product.category).replace('-', ' ')}
                  </span>
                </div>
              )}

              {images.length > 1 && (
                <>
                  <button title='back' onClick={prevImage} aria-label="Previous image" className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all">
                    <ChevronLeft className="w-5 h-5 text-[#3D3D3D]" />
                  </button>
                  <button title='next' onClick={nextImage} aria-label="Next image" className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all">
                    <ChevronRight className="w-5 h-5 text-[#3D3D3D]" />
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {images.map((_, index) => (
                      <button
                        title={`Select image ${index + 1}`}
                        key={index}
                        onClick={() => setSelectedImageIndex(index)}
                        className={`w-2 h-2 rounded-full transition-all ${index === selectedImageIndex ? 'bg-white w-8' : 'bg-white/50 hover:bg-white/70'}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            {images.length > 1 && (
              <div className="grid grid-cols-4 gap-3">
                {images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`relative rounded-xl overflow-hidden aspect-square transition-all ${index === selectedImageIndex ? 'ring-2 ring-[#D4A5A5]' : 'opacity-60 hover:opacity-100'}`}
                  >
                    <img src={image} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="lg:sticky lg:top-32">
            <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-[#3D3D3D] mb-4">{product.title}</h1>
            <div className="flex items-center gap-4 mb-6">
              <span className="font-sans text-3xl font-semibold text-[#D4A5A5]">${product.price.toFixed(2)}</span>
              {product.pagesCount && (
                <span className="font-sans text-sm text-[#3D3D3D]/60 flex items-center gap-1 bg-[#3D3D3D]/5 px-3 py-1 rounded-full">
                  <Download className="w-4 h-4" />
                  {product.pagesCount} pages included
                </span>
              )}
            </div>
            {product.description && (
              <p className="font-sans text-[#3D3D3D]/70 leading-relaxed mb-8" dangerouslySetInnerHTML={{ __html: product.description }} />
            )}

            <div className="space-y-3">
              <button onClick={handleAddToCart} disabled={addedToCart} className="w-full bg-[#3D3D3D] hover:bg-[#2D2D2D] text-white py-6 rounded-full font-sans text-lg">
                {addedToCart ? (
                  <>
                    <Check className="w-5 h-5 mr-2 inline" /> Added to Cart
                  </>
                ) : (
                  <>
                    <ShoppingBag className="w-5 h-5 mr-2 inline" /> Add to Cart
                  </>
                )}
              </button>
              {addedToCart && (
                <button onClick={() => navigate('/cart')} className="w-full border border-[#D4A5A5] text-[#D4A5A5] hover:bg-[#D4A5A5]/10 py-6 rounded-full font-sans">
                  View Cart
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
